import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { useBrand } from '../context/BrandContext';

// Generate FAQ data with brand-specific values
const getFaqData = (brandName, supportEmail) => [
  {
    category: 'Selling & Commissions',
    questions: [
      {
        q: `How do I earn money on ${brandName}?`,
        a: 'You earn 10% commission on every sale of products you create. When you make a product public, anyone can buy it, and you earn money automatically!'
      },
      {
        q: 'What is the 10% creator commission?',
        a: 'When someone purchases a product you created, you automatically earn 10% of the sale price. This is credited to your account and paid out monthly.'
      },
      {
        q: 'What is the 5% referral bonus?',
        a: "When you share a product link and someone makes a purchase through your link, you earn an additional 5% referral commission - even on products you didn't create!"
      },
      {
        q: 'When do I get paid?',
        a: 'Commissions are calculated after the order is fulfilled and the return window has passed. Payouts are processed monthly for balances over $25.'
      },
      {
        q: 'How do I track my earnings?',
        a: 'Your earnings dashboard shows all your commissions, pending payouts, and payment history. You can access it from your profile menu.'
      }
    ]
  },
  {
    category: 'Products & Publishing',
    questions: [
      {
        q: 'What does "public" vs "private" mean?',
        a: 'Public products appear in the store for everyone to see and buy. Private products are only visible to you. You can change this anytime using the eye icon on your product.'
      },
      {
        q: 'Can I edit my products after publishing?',
        a: 'Yes! You can edit the design placement, change mockups, and update visibility at any time. Click the edit icon on any product you own.'
      },
      {
        q: 'How long does shipping take?',
        a: 'Most products ship within 2-5 business days. Delivery typically takes 5-10 additional days depending on your location.'
      },
      {
        q: 'What happens when someone buys my product?',
        a: `The order is automatically sent to an independent print provider who prints and ships directly to the customer. ${brandName} does not handle or inspect any products. You earn your commission once the order is fulfilled.`
      }
    ]
  },
  {
    category: 'Getting Started',
    questions: [
      {
        q: 'How do I create a product?',
        a: "Upload your image or use our AI to generate one, select product categories (t-shirts, mugs, etc.), and click Create Products. It's that simple!"
      },
      {
        q: 'What file formats are supported?',
        a: 'We support PNG, JPG, and WEBP images. For best results, use high-resolution images (at least 2000x2000 pixels) with transparent backgrounds for designs.'
      },
      {
        q: 'How does AI image generation work?',
        a: 'Our AI can generate custom designs from your text descriptions. Just describe what you want, and the AI creates unique artwork ready for your products.'
      },
      {
        q: 'Do I need to handle shipping or customer service?',
        a: `No! Our print partners handle all printing and shipping. For customer inquiries about orders, buyers can contact the print provider's support directly or email ${supportEmail} for guidance on filing claims. You just create awesome designs and earn money when they sell.`
      }
    ]
  },
  {
    category: 'Sharing & Referrals',
    questions: [
      {
        q: 'How do I share my products?',
        a: 'Click the share button on any product to get your unique referral link. Share it on social media, in messages, or anywhere online!'
      },
      {
        q: 'How does affiliate tracking work?',
        a: 'When someone clicks your share link, they get a cookie that tracks them for 30 days. If they make any purchase within that time, you earn the 5% referral bonus.'
      },
      {
        q: 'Can I earn from sharing products I did not create?',
        a: "Yes! You earn 5% referral commission on any product sold through your link, even if you didn't create it."
      }
    ]
  }
];

const FAQ = () => {
  const [openItems, setOpenItems] = useState({});
  const brand = useBrand();

  const brandName = brand?.name || 'MerchLike';
  const supportEmail = brand?.legal?.supportEmail || 'support@merchlike.com';
  const studioRoute = brand?.routes?.studio || '/studio-x7k9m2';
  const faqData = getFaqData(brandName, supportEmail);

  const toggleItem = (categoryIndex, questionIndex) => {
    const key = `${categoryIndex}-${questionIndex}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <Link
            to={studioRoute}
            className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-4"
          >
            <ArrowLeft size={20} />
            Back to Studio
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Frequently Asked Questions</h1>
          <p className="text-slate-600 mt-2">Everything you need to know about selling on {brandName}</p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        {faqData.map((category, catIndex) => (
          <div key={catIndex} className="mb-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">{category.category}</h2>
            <div className="space-y-2">
              {category.questions.map((item, qIndex) => {
                const key = `${catIndex}-${qIndex}`;
                const isOpen = openItems[key];
                return (
                  <div key={qIndex} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                    <button
                      onClick={() => toggleItem(catIndex, qIndex)}
                      className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50"
                    >
                      <span className="font-medium text-slate-900">{item.q}</span>
                      {isOpen ? (
                        <ChevronUp size={20} className="text-slate-400 flex-shrink-0" />
                      ) : (
                        <ChevronDown size={20} className="text-slate-400 flex-shrink-0" />
                      )}
                    </button>
                    {isOpen && (
                      <div className="px-4 pb-4 text-slate-600">
                        {item.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Contact Section */}
        <div className="mt-12 p-6 bg-blue-50 rounded-xl border border-blue-200 text-center">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Still have questions?</h3>
          <p className="text-slate-600 mb-4">
            We're here to help! Reach out to our support team.
          </p>
          <a
            href={`mailto:${supportEmail}`}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Contact Support
          </a>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-slate-200 mt-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          <p>&copy; {brand?.legal?.copyrightYear || '2025'} {brand?.legal?.copyrightText || brandName}. All rights reserved.</p>
          <div className="flex justify-center gap-4 mt-2">
            <Link to="/terms" className="hover:text-slate-700">Terms of Service</Link>
            <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
